import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Notification service — replaces AngularJS toastr / notification services.
 *
 * Provides a simple notification queue with auto-dismiss behavior,
 * matching the existing toast notifications in the app.
 */

type NotificationType = 'success' | 'error' | 'warning' | 'info'

interface Notification {
    id: string
    type: NotificationType
    title?: string
    message: string
    timestamp: number
}

interface NotificationContextType {
    notifications: Notification[]
    success: (message: string, title?: string) => void
    error: (message: string, title?: string) => void
    warning: (message: string, title?: string) => void
    info: (message: string, title?: string) => void
    dismiss: (id: string) => void
    dismissAll: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

let notificationCounter = 0

interface NotificationProviderProps {
    children: ReactNode
    /** Auto-dismiss duration in ms. Default: 5000 */
    autoDismissMs?: number
}

export function NotificationProvider({
    children,
    autoDismissMs = 5000,
}: NotificationProviderProps) {
    const [notifications, setNotifications] = useState<Notification[]>([])

    const addNotification = useCallback(
        (type: NotificationType, message: string, title?: string) => {
            const id = `notification-${++notificationCounter}`
            const notification: Notification = {
                id,
                type,
                title,
                message,
                timestamp: Date.now(),
            }

            setNotifications((prev) => [...prev, notification])

            // Auto-dismiss after duration (except errors)
            if (type !== 'error') {
                setTimeout(() => {
                    setNotifications((prev) => prev.filter((n) => n.id !== id))
                }, autoDismissMs)
            }
        },
        [autoDismissMs],
    )

    const dismiss = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, [])

    const dismissAll = useCallback(() => {
        setNotifications([])
    }, [])

    const success = useCallback(
        (msg: string, title?: string) => addNotification('success', msg, title),
        [addNotification],
    )
    const error = useCallback(
        (msg: string, title?: string) => addNotification('error', msg, title),
        [addNotification],
    )
    const warning = useCallback(
        (msg: string, title?: string) => addNotification('warning', msg, title),
        [addNotification],
    )
    const info = useCallback(
        (msg: string, title?: string) => addNotification('info', msg, title),
        [addNotification],
    )

    const value = useMemo(
        () => ({ notifications, success, error, warning, info, dismiss, dismissAll }),
        [notifications, success, error, warning, info, dismiss, dismissAll],
    )

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {notifications.length > 0 && createPortal(
                <div id="toast-container" className="toast-top-right" style={{ position: 'fixed', top: 12, right: 12, zIndex: 999999 }}>
                    {notifications.map((n) => (
                        <div
                            key={n.id}
                            className={`toast toast-${n.type}`}
                            style={{
                                display: 'block',
                                padding: '15px 15px 15px 50px',
                                margin: '0 0 6px',
                                width: 300,
                                borderRadius: 3,
                                backgroundPosition: '15px center',
                                backgroundRepeat: 'no-repeat',
                                boxShadow: '0 0 12px rgba(0,0,0,.35)',
                                color: '#fff',
                                opacity: 0.95,
                                cursor: 'pointer',
                                ...(n.type === 'error' ? { backgroundColor: '#bd362f' } : {}),
                                ...(n.type === 'success' ? { backgroundColor: '#51a351' } : {}),
                                ...(n.type === 'warning' ? { backgroundColor: '#f89406' } : {}),
                                ...(n.type === 'info' ? { backgroundColor: '#2f96b4' } : {}),
                            }}
                            onClick={() => dismiss(n.id)}
                            role="alert"
                        >
                            {n.title && <div className="toast-title" style={{ fontWeight: 'bold' }}>{n.title}</div>}
                            <div className="toast-message">{n.message}</div>
                        </div>
                    ))}
                </div>,
                document.body,
            )}
        </NotificationContext.Provider>
    )
}

export function useNotifications(): NotificationContextType {
    const context = useContext(NotificationContext)
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider')
    }
    return context
}
